import {
  useModalsContext,
  useSendingPlatformsQueryContext,
} from 'modules/sendingPlatforms/contexts';
import { useDeleteSmsSender } from 'modules/sendingPlatforms/hooks/useDeleteSmsSender';
import { SmsSender } from 'modules/sendingPlatforms/types';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { BUTTON_SIZES, DestructiveButton } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { Body2 } from 'ui/theme';

export const DELETE_SMS_SENDER_TEXT = {
  bodyText: `If you proceed, we’ll disconnect the messaging service account from your Iterable project.
    If you want to send from this messaging profile again, you can set it up again in the future.`,
  buttonText: 'Disconnect message profile',
  errorMessage: 'Unable to disconnect messaging service.',
  headerText: 'Disconnect message profile?',
  id: 'edit-account-integration-modal',
  successMessage: 'Success! Disconnected:',
};

export const BodyText = ({ name }: { name: string }) => {
  const [prefix, suffix] = DELETE_SMS_SENDER_TEXT.bodyText.split('messaging service');
  return (
    <Body2>
      {prefix}
      <b>{name}</b>
      {suffix}
    </Body2>
  );
};

type Props = {
  smsSender: SmsSender;
};

export const DeleteSmsSenderModal = ({ smsSender }: Props) => {
  const { hideModal } = useModalsContext();
  const dispatch = useDispatch();
  const { refetchSmsSenders } = useSendingPlatformsQueryContext();
  const { deleteSmsSender, loading } = useDeleteSmsSender({
    onCompleted: () => {
      dispatch(
        addPositiveToast(
          <>
            {DELETE_SMS_SENDER_TEXT.successMessage} <b>{smsSender.name}</b>
          </>,
        ),
      );
      refetchSmsSenders();
      hideModal();
    },
    onError: (err) => {
      const { message = '' } = err;
      dispatch(addNegativeToast(`${DELETE_SMS_SENDER_TEXT.errorMessage} ${message}`));
      hideModal();
    },
  });

  const onSubmit = () => {
    deleteSmsSender({ variables: { id: smsSender.id } });
  };

  return (
    <Modal
      id={DELETE_SMS_SENDER_TEXT.id}
      isModalOpen={true}
      onClose={hideModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
    >
      <ModalHeader>{DELETE_SMS_SENDER_TEXT.headerText}</ModalHeader>
      <ModalBody>
        <BodyText name={smsSender.name} />
      </ModalBody>
      <ModalFooter>
        <DestructiveButton isLoading={loading} onClick={onSubmit} size={BUTTON_SIZES.LARGE}>
          {DELETE_SMS_SENDER_TEXT.buttonText}
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
